export type JudgeCaseStatus = 'hearing_set' | 'awaiting_ruling' | 'adjourned' | 'closed';

export interface JudgeCase {
  ref: string;
  title: string;
  type: string;
  amount: string;
  status: JudgeCaseStatus;
  hearingDate?: string;   // e.g. "18 Jan · 10:00"
  division: string;
}

export interface HearingCard {
  ref: string;
  title: string;
  day: string;
  month: string;
  time: string;
  division: string;
  type: string;
  note: string;
  amount: string;
}

export interface RulingCard {
  caseFilingId?: string;   // real case UUID (used by action endpoints)
  ref: string;
  title: string;
  amount: string;
  type: string;
  daysElapsed: number;    // days since last hearing; >30 = SLA breach
  urgency: 'normal' | 'warn' | 'breach';
}

export interface ProcedureBriefDoc {
  label: string;
  pages: string;
  ocr: number;
  status: 'complete' | 'partial' | 'missing';
}

export interface ProcedureBrief {
  caseFilingId?: string;   // real case UUID (used by action endpoints)
  ref: string;
  title: string;
  type: string;
  amount: string;
  division: string;
  summary: string;
  claimant: string;
  claimantCounsel?: string;
  defendant: string;
  defendantCounsel?: string;
  legalIssues: { title: string; detail: string }[];
  documents: ProcedureBriefDoc[];
  riskFlags: { level: 'info' | 'warn' | 'error'; message: string }[];
  hearingHistory: { label: string; meta: string; status: 'done' | 'active' | 'pending' }[];
  sla: { daysAssigned: number; deadline: string; withinSla: boolean };
}

// ── Mock data ──────────────────────────────────────────────────

export const MOCK_JUDGE_METRICS = {
  activeCases: 12,
  upcomingHearings: 2,
  pendingRulings: 3,
  adjourned: 1,
};

export const MOCK_JUDGE_CASES: JudgeCase[] = [
  { ref: 'LC/2025/00121', title: 'Adewale v. Sunrise Properties Ltd.',  type: 'Debt recovery',    amount: '₦7.2M',  status: 'hearing_set',     hearingDate: '18 Jan · 10:00', division: 'Commercial' },
  { ref: 'LC/2025/00098', title: 'Mensah v. Volta Engineering',          type: 'Contract dispute', amount: '₦15M',   status: 'awaiting_ruling',  hearingDate: '—',              division: 'Commercial' },
  { ref: 'LC/2025/00105', title: 'Akintola v. CrestBridge Ltd.',         type: 'Debt recovery',    amount: '₦3.8M',  status: 'awaiting_ruling',  hearingDate: '—',              division: 'Commercial' },
  { ref: 'LC/2025/00112', title: 'Ogbonna v. Stellar Imports',           type: 'Contract dispute', amount: '₦9.1M',  status: 'adjourned',        hearingDate: 'TBD',            division: 'Commercial' },
  { ref: 'LC/2025/00130', title: 'Danjuma v. Heritage Homes',            type: 'Contract dispute', amount: '₦4.2M',  status: 'hearing_set',      hearingDate: '22 Jan · 14:00', division: 'Commercial' },
];

export const MOCK_HEARINGS: HearingCard[] = [
  {
    ref: 'LC/2025/00121',
    title: 'Adewale v. Sunrise Properties',
    day: '18', month: 'JAN',
    time: '10:00 AM',
    division: 'Commercial Division',
    type: 'First hearing',
    note: 'Both parties confirmed',
    amount: '₦7.2M',
  },
  {
    ref: 'LC/2025/00130',
    title: 'Danjuma v. Heritage Homes',
    day: '22', month: 'JAN',
    time: '14:00 PM',
    division: 'Commercial Division',
    type: 'Second hearing',
    note: 'Defence counsel requested adjournment',
    amount: '₦4.2M',
  },
];

export const MOCK_RULINGS: RulingCard[] = [
  { ref: 'LC/2025/00098', title: 'Mensah v. Volta Engineering',       amount: '₦15,000,000', type: 'Contract dispute', daysElapsed: 18, urgency: 'normal' },
  { ref: 'LC/2025/00105', title: 'Akintola v. CrestBridge Ltd.',      amount: '₦3,800,000',  type: 'Debt recovery',    daysElapsed: 27, urgency: 'warn'   },
  { ref: 'LC/2024/00987', title: 'Okafor v. Standard Industries',     amount: '₦22,000,000', type: 'Contract breach',  daysElapsed: 42, urgency: 'breach' },
];

export const MOCK_PROCEDURAL_BRIEF: ProcedureBrief = {
  ref: 'LC/2025/00121',
  title: 'Adewale v. Sunrise Properties Ltd.',
  type: 'Debt recovery',
  amount: '₦7,200,000',
  division: 'Commercial Division',
  summary: 'Claimant seeks recovery of ₦7,200,000 from Sunrise Properties Ltd. for unpaid property renovation services completed between March–August 2024. Defendant acknowledges work completion but disputes final valuation. Contract specifies milestone payments with retention clause. Claimant has furnished signed contract, completion certificate, and 6 invoices.',
  claimant: 'Mr Oluwatobi B. Adewale',
  claimantCounsel: 'represented by self',
  defendant: 'Sunrise Properties Ltd. (RC-112045)',
  defendantCounsel: 'Counsel: Barr. A. Okonkwo',
  legalIssues: [
    { title: '1. Quantum dispute', detail: 'Contract value ₦7.2M agreed, defendant disputes retention deductions. AI identifies milestone schedule in contract clause 4.2.' },
    { title: '2. Completion certification', detail: 'Completion certificate signed by both parties. AI confidence: high.' },
    { title: '3. Interest claim', detail: 'Claimant claims interest at 18% p.a. per contract clause 9.1. Pre-judgment interest may apply per Order 39 Rule 7.' },
  ],
  documents: [
    { label: 'Renovation Contract',    pages: '12 pages', ocr: 96, status: 'complete' },
    { label: 'Completion Certificate', pages: '2 pages',  ocr: 98, status: 'complete' },
    { label: 'Invoices (6)',            pages: '18 pages', ocr: 94, status: 'complete' },
  ],
  riskFlags: [
    { level: 'info',  message: 'All required documents present. Completeness: 94/100.' },
    { level: 'warn',  message: 'Defendant may raise limitation defence — check if claim within 6 years.' },
    { level: 'info',  message: 'Filing fees confirmed via Remita. RRR: 340917284053.' },
  ],
  hearingHistory: [
    { label: 'Pre-trial conference', meta: '8 Jan 2025 · Court 9',    status: 'done'   },
    { label: 'Hearing scheduled',    meta: '18 Jan 2025 · 10:00',     status: 'active' },
  ],
  sla: { daysAssigned: 14, deadline: '30 days max', withinSla: true },
};
